import { Injectable } from '@angular/core';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '/';

  parse(value: string): NgbDateStruct | null {
    const parsedDate = this.parseDate(value);

    if (parsedDate) {
      return parsedDate;
    }

    return null;
  }

  format(date: NgbDateStruct): string {
    if (!date) {
      return '';
    }

    const day = this.isNumber(date.day) ? this.padNumber(date.day) : '';
    const month = this.isNumber(date.month) ? this.padNumber(date.month) : '';
    const year = date.year.toString().padStart(4, '0'); // always 4 digits

    return `${day}${this.DELIMITER}${month}${this.DELIMITER}${year}`;
  }

  private parseDate(value: string): NgbDateStruct | null {
    const trimmedValue = value?.trim();

    if (!trimmedValue) {
      return null;
    }

    const separatedDate = this.parseSeparatedDate(trimmedValue);
    if (separatedDate) {
      return separatedDate;
    }

    return this.parseCompactDate(trimmedValue);
  }

  private parseSeparatedDate(value: string): NgbDateStruct | null {
    const dateParts = value.split(/[./-]/);

    if (
      dateParts.length !== 3 ||
      !dateParts.every((part) => /^\d+$/.test(part))
    ) {
      return null;
    }

    const [first, second, third] = dateParts.map((part) => this.toInteger(part));

    if (dateParts[0].length === 4) {
      // e.g. "2025-08-21"
      return this.toValidDate(first, second, third);
    }

    // e.g. "21/08/25", "21-08-2025", or "21.08.2025"
    return this.toValidDate(this.expandYear(third), second, first);
  }

  private parseCompactDate(value: string): NgbDateStruct | null {
    if (!/^\d{6}$|^\d{8}$/.test(value)) {
      return null;
    }

    const day = this.toInteger(value.slice(0, 2));
    const month = this.toInteger(value.slice(2, 4));
    const yearValue = this.toInteger(value.slice(4));

    return this.toValidDate(this.expandYear(yearValue), month, day);
  }

  private toValidDate(year: number, month: number, day: number): NgbDateStruct | null {
    const candidate = new Date(year, month - 1, day);

    if (
      candidate.getFullYear() !== year ||
      candidate.getMonth() !== month - 1 ||
      candidate.getDate() !== day
    ) {
      return null;
    }

    return { year, month, day };
  }

  private expandYear(year: number): number {
    return year < 100 ? 2000 + year : year;
  }

  padNumber(value: number): string {
    if (this.isNumber(value)) {
      return `0${value}`.slice(-2);
    }
    return '';
  }

  isNumber(value: any): boolean {
    return !isNaN(this.toInteger(value));
  }

  toInteger(value: any): number {
    return parseInt(`${value}`, 10);
  }
}
